const PRESENCA_DIGITAL_SUFFIXES = ['.com.br', '.com', '.net'] as const;

const PRESENCA_DIGITAL_CHAR_REGEX = /^[a-z0-9àáâãéêíóôõúüç-]+$/;
const PRESENCA_DIGITAL_HAS_LETTER_REGEX = /[a-zàáâãéêíóôõúüç]/;

/**
 * Valida sintaticamente um hostname/domínio DNS completo.
 * Aceita qualquer TLD bem formada (.io, .tech, .com.br, .co.uk, etc.).
 * Não verifica se a extensão existe na IANA — isso fica a cargo do endpoint.
 * Rejeita qualquer espaço em branco (início, meio ou fim).
 */
export function isValidDomain(domain: string): boolean {
  if (!domain || typeof domain !== 'string') {
    return false;
  }

  // Espaço em qualquer posição invalida — não usar trim para “limpar”
  if (/\s/.test(domain)) {
    return false;
  }

  const normalized = domain.toLowerCase();

  if (!normalized || normalized.length > 253) {
    return false;
  }

  // Rejeita URL, e-mail, caminho, porta e underscore
  if (/[/:@?#\\_]/.test(normalized)) {
    return false;
  }

  if (normalized.includes('..') || normalized.startsWith('.') || normalized.endsWith('.')) {
    return false;
  }

  const labels = normalized.split('.');
  if (labels.length < 2) {
    return false;
  }

  const labelRegex = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i;

  for (const label of labels) {
    if (label.length < 1 || label.length > 63) {
      return false;
    }
    if (!labelRegex.test(label)) {
      return false;
    }
  }

  // TLD: pelo menos 2 caracteres alfabéticos
  const tld = labels[labels.length - 1];
  if (tld.length < 2 || !/^[a-z]{2,}$/i.test(tld)) {
    return false;
  }

  return true;
}

/**
 * Valida o rótulo digitado quando o produto acrescenta um sufixo fixo
 * (ex.: Microsoft → ".onmicrosoft.com").
 * Rejeita qualquer espaço em branco (início, meio ou fim).
 */
export function isValidDomainLabel(label: string): boolean {
  if (!label || typeof label !== 'string') {
    return false;
  }

  if (/\s/.test(label)) {
    return false;
  }

  const normalized = label.toLowerCase();

  if (!normalized || normalized.length > 63) {
    return false;
  }

  if (/[./:@?#\\_]/.test(normalized)) {
    return false;
  }

  return /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/i.test(normalized);
}

/**
 * Valida o rótulo de domínio para produtos de presença digital
 * (sufixos .com.br, .com, .net) conforme regras sintáticas do Registro.br.
 */
export function isValidPresencaDigitalDomainLabel(label: string): boolean {
  if (!label || typeof label !== 'string') {
    return false;
  }

  if (/\s/.test(label)) {
    return false;
  }

  const normalized = label.toLowerCase();

  if (normalized.length < 2 || normalized.length > 26) {
    return false;
  }

  if (normalized.startsWith('-') || normalized.endsWith('-')) {
    return false;
  }

  if (!PRESENCA_DIGITAL_CHAR_REGEX.test(normalized)) {
    return false;
  }

  // Não conter somente números
  if (!PRESENCA_DIGITAL_HAS_LETTER_REGEX.test(normalized)) {
    return false;
  }

  return true;
}

/** Sufixos de presença digital (.com.br, .com, .net). */
export function isPresencaDigitalDomainSuffix(suffix?: string | null): boolean {
  if (!suffix) {
    return false;
  }
  return (PRESENCA_DIGITAL_SUFFIXES as readonly string[]).includes(suffix);
}

/**
 * Escolhe o validador adequado conforme o sufixo do produto.
 * - .com.br / .com / .net → presença digital
 * - outro sufixo (ex. .onmicrosoft.com) → label DNS
 * - sem sufixo (Google) → hostname completo
 */
export function isValidDomainInput(domain: string, domainSuffix?: string | null): boolean {
  if (isPresencaDigitalDomainSuffix(domainSuffix)) {
    return isValidPresencaDigitalDomainLabel(domain);
  }
  if (domainSuffix) {
    return isValidDomainLabel(domain);
  }
  return isValidDomain(domain);
}
